import { ReactNode, useMemo, useRef, useState } from "react";
import cn from "classnames";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import { ObjectHelper } from "src/shared/helpers/ObjectHelper";

import { ClickOutside } from "../ClickOutside";

import { useStyles } from "./styles";
import { defaultProps } from "./constants";

interface ISelectOption<T = any> {
    value: T;
    label: string;
    icon?: ReactNode;
    rake?: string;
}

export type TDropdownPosition = "top" | "bottom";

export interface IDropdownProps {
    position?: TDropdownPosition;
    className?: string;
}

export interface ISelectProps<T> {
    className?: string;
    type?: "primary" | "secondary";
    emptyLabel?: string;
    emptyIcon?: ReactNode;
    value: T;
    onChange: (value: T) => void;
    options: ISelectOption<T>[];
    renderEmptyLabel?: () => ReactNode;
    renderLabel?: (option: ISelectOption<T>) => ReactNode;
    renderOption?: (option: ISelectOption<T>, selected: boolean) => ReactNode;
    dropdownProps?: IDropdownProps;
    isMatch?: boolean;
}

export const Select = <T,>(props: ISelectProps<T>) => {
    const classes = useStyles();
    const [isDropdownOpen, setDropdownOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    const {
        value,
        onChange,
        type,
        className,
        options,
        emptyLabel,
        emptyIcon,
        renderEmptyLabel,
        renderLabel,
        renderOption,
        dropdownProps,
        isMatch,
    } = ObjectHelper.merge(defaultProps, props);

    const onSelectValue = (value: T) => {
        onChange(value);
    };

    const onDropdownClose = () => {
        setDropdownOpen(false);
    };

    const onDropdownToggle = () => {
        setDropdownOpen(!isDropdownOpen);
    };

    const selectedOption = useMemo(() => {
        const option = options.find((option) => option.value === value);

        return option ?? { value: null, label: emptyLabel, icon: emptyIcon };
    }, [value, options, emptyLabel, emptyIcon]);

    return (
        <ClickOutside reference={ref} onClickOutside={onDropdownClose}>
            <div
                className={cn(
                    classes.selectWrapper,
                    className,
                    classes[`selectWrapper-${type ?? "primary"}`],
                    {
                        prolimit: isMatch == false,
                    }
                )}
                ref={ref}
            >
                <div
                    className={cn(classes.selectedValue)}
                    onClick={onDropdownToggle}
                >
                    {!selectedOption && renderEmptyLabel
                        ? renderEmptyLabel()
                        : renderLabel?.(selectedOption as ISelectOption<T>) ?? (
                              <div className={classes.selectedOptionWrapper}>
                                  {selectedOption.icon}
                                  <span
                                      className={cn(
                                          classes.selectedValueLabel,
                                          {
                                              hasIcon: Boolean(
                                                  selectedOption.icon
                                              ),
                                          }
                                      )}
                                  >
                                      {selectedOption.label}
                                  </span>
                              </div>
                          )}
                    <ExpandMoreIcon
                        className={cn(classes.expandIcon, {
                            isRotate: isDropdownOpen,
                        })}
                    />
                </div>
                <div
                    className={cn(
                        classes.selectDropdownWrapper,
                        dropdownProps.position,
                        classes[`selectDropdownWrapper-primary`],
                        {
                            isOpen: isDropdownOpen,
                        }
                    )}
                >
                    {options.map((option) => (
                        <div
                            onClick={() => onSelectValue(option.value)}
                            className={cn(classes.selectOption, {
                                isSelected: value === option.value,
                                [classes.selectOptionDefaultStyles]:
                                    !Boolean(renderOption),
                            })}
                        >
                            {renderOption?.(option, value === option.value) ?? (
                                <>
                                    {option.icon}
                                    <span
                                        className={cn(
                                            classes.selectOptionLabel,
                                            {
                                                hasIcon: Boolean(option.icon),
                                            }
                                        )}
                                    >
                                        {option.label}
                                    </span>
                                </>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        </ClickOutside>
    );
};
